import type { Hue, Tag, TaskInstance, User } from '@hestia/core';
import { describeRule } from '@hestia/core';

/** Colour means exactly two things: who owns a task, and which list it is in.
 *  These helpers are the only place a hue is chosen, so that rule cannot drift. */
export const hueVar = (h: Hue | string) => `var(--${h})`;
export const tintVar = (h: Hue | string) => `var(--${h}-tint)`;
export const onTintVar = (h: Hue | string) => `var(--${h}-on)`;

export function Icon({ path, size = 18, w }: { path: string; size?: number; w?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={w ? { strokeWidth: w } : undefined}
         dangerouslySetInnerHTML={{ __html: path }} />
  );
}

export const icons = {
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  calendar: '<rect x="3" y="5" width="18" height="16" rx="4"/><path d="M8 3v4M16 3v4M3 10h18"/>',
  repeat: '<path d="M17 2.5 20.5 6 17 9.5"/><path d="M3.5 11.5v-1a4 4 0 0 1 4-4h13"/><path d="M7 21.5 3.5 18 7 14.5"/><path d="M20.5 12.5v1a4 4 0 0 1-4 4h-13"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  check: '<path d="m5 13 4 4L19 7"/>',
  arrowRight: '<path d="M5 12h14M13 6l6 6-6 6"/>',
  chevronLeft: '<path d="m14.5 6-6 6 6 6"/>',
  chevronRight: '<path d="m9.5 6 6 6-6 6"/>',
  list: '<path d="M8 6h12M8 12h12M8 18h12M3.6 6h.01M3.6 12h.01M3.6 18h.01"/>',
  users: '<circle cx="9" cy="8" r="3.2"/><path d="M3 20a6 6 0 0 1 12 0"/><path d="M16 5.4a3.2 3.2 0 0 1 0 5.2"/><path d="M17.6 14.6A6 6 0 0 1 21 20"/>',
  tag: '<path d="M3 11V4h7l10 10-7 7L3 11z"/><circle cx="7" cy="8" r="1.3"/>',
  trash: '<path d="M4 7h16M10 11v6M14 11v6M6.5 7l1 13h9l1-13M9 7V4h6v3"/>',
  x: '<path d="M6 6l12 12M18 6 6 18"/>',
  note: '<path d="M5 4.5h14v15H5z" rx="3"/><path d="M8.5 9h7M8.5 13h7M8.5 17h4"/>',
  mic: '<rect x="9" y="2.5" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0"/><path d="M12 17.5V21"/>',
  sparkle: '<path d="M12 3.5 13.7 9l5.5 1.7-5.5 1.7L12 18l-1.7-5.6L4.8 10.7 10.3 9z"/><path d="M18.5 3v3M20 4.5h-3"/>',
  span: '<path d="M4 12h16"/><path d="M7.5 8.5 4 12l3.5 3.5"/><path d="M16.5 8.5 20 12l-3.5 3.5"/>',
  trashSm: '<path d="M5 7h14M10 11v5M14 11v5M7 7l.8 12h8.4L17 7M9.5 7V4.5h5V7"/>',
  cloudOff: '<path d="M6.5 18.5A4.5 4.5 0 0 1 6 9.6a6 6 0 0 1 9.3-3.4"/><path d="M17.5 9.2a4.5 4.5 0 0 1 1.3 8.8H10"/><path d="M3 3l18 18"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 7.6h.01"/>',
  settings: '<path d="M9.80 5.04 L10.25 2.56 L13.75 2.56 L14.20 5.04 L15.37 5.52 L17.44 4.09 L19.91 6.56 L18.48 8.63 L18.96 9.80 L21.44 10.25 L21.44 13.75 L18.96 14.20 L18.48 15.37 L19.91 17.44 L17.44 19.91 L15.37 18.48 L14.20 18.96 L13.75 21.44 L10.25 21.44 L9.80 18.96 L8.63 18.48 L6.56 19.91 L4.09 17.44 L5.52 15.37 L5.04 14.20 L2.56 13.75 L2.56 10.25 L5.04 9.80 L5.52 8.63 L4.09 6.56 L6.56 4.09 L8.63 5.52Z"/><circle cx="12" cy="12" r="3.1"/>',
  /* The wall panel: a frame with an agenda on it. */
  panel: '<rect x="3" y="4" width="18" height="15" rx="3"/><path d="M7 9h4"/><path d="M7 13h10"/><path d="M7 16h6"/>',
};

/** Hestia's hearth — the same flame as the home-screen icon, drawn from the same
 *  path, so the thing in the sidebar and the thing on your phone are recognisably
 *  one app. Kept in sync with `public/icon.svg` by hand: it is one path, and
 *  wiring a build step to share it would cost more than it saves. */
const FLAME = 'M 51 21 C 58.5 33 71 44 71 59 C 71 70.6 61.6 80 50 80 '
  + 'C 38.4 80 29 70.6 29 59 C 29 50 34.5 44.5 38 39 '
  + 'C 39.8 44.5 42.5 47 45 48 C 46.5 39 48.5 29 51 21 Z';

export function Logo({ size = 34 }: { size?: number }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: size * 0.35, flex: 'none',
      background: 'radial-gradient(120% 110% at 50% 42%, #3C2415 0%, #1B100A 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width={size * 0.78} height={size * 0.78} viewBox="18 12 64 76" aria-hidden>
        <defs>
          <linearGradient id="logo-fl" x1=".28" y1="0" x2=".72" y2="1">
            <stop offset="0" stopColor="#FFD063" /><stop offset=".42" stopColor="#FF8A2B" />
            <stop offset="1" stopColor="#E23F17" />
          </linearGradient>
          <linearGradient id="logo-core" x1=".3" y1="0" x2=".7" y2="1">
            <stop offset="0" stopColor="#FFFBF0" /><stop offset="1" stopColor="#FFD98F" />
          </linearGradient>
        </defs>
        <path d={FLAME} fill="url(#logo-fl)" stroke="none" />
        <path d={FLAME} fill="url(#logo-core)" stroke="none" transform="translate(30 43) scale(0.4)" />
      </svg>
    </span>
  );
}

export function Avatar({ user, size = 28, muted }: { user?: User | null; size?: number; muted?: boolean }) {
  if (!user) {
    return <span className="avatar" style={{ width: size, height: size, background: 'var(--ink-4)' }} />;
  }
  // A finished row carries no fully-saturated colour, but it must still be the
  // right *person's* colour — mixing their own hue toward white does both.
  const background = muted
    ? `color-mix(in oklab, ${hueVar(user.hue)} 42%, white)`
    : hueVar(user.hue);
  return (
    <span className="avatar" title={user.displayName}
          style={{ width: size, height: size, fontSize: Math.round(size * 0.4), background }}>
      {user.displayName.slice(0, 1).toUpperCase()}
    </span>
  );
}

export function TagPill({ tag, onRemove }: { tag: Tag; onRemove?: () => void }) {
  return (
    <span className="tag" style={{ background: tintVar(tag.hue), color: onTintVar(tag.hue) }}>
      {tag.name}
      {onRemove && (
        <button aria-label={`Remove ${tag.name}`} onClick={onRemove}
                style={{ padding: 0, marginLeft: 5, display: 'flex', color: 'inherit', opacity: .6 }}>
          <Icon path={icons.x} size={11} w={2.4} />
        </button>
      )}
    </span>
  );
}

/** A task's tags, resolved against the tag map the app already holds. */
export function TagPills({ ids, tags }: { ids: readonly string[]; tags: Map<string, Tag> }) {
  return <>{ids.map((id) => { const t = tags.get(id); return t ? <TagPill key={id} tag={t} /> : null; })}</>;
}

export function RepeatChip({ rrule, muted }: { rrule: string | null; muted?: boolean }) {
  const label = describeRule(rrule, { short: true });
  if (!label) return null;
  return (
    <span className="chip" style={muted ? { color: 'var(--ink-3)' } : undefined}>
      <Icon path={icons.repeat} size={13} w={2.2} />{label}
    </span>
  );
}

export function timeLabel(i: TaskInstance): string | null {
  const { startTime, durationMin } = i.task;
  if (!startTime) return null;
  if (!durationMin) return startTime;
  const [h, m] = startTime.split(':').map(Number) as [number, number];
  const end = h * 60 + m + durationMin;
  const hh = String(Math.floor(end / 60) % 24).padStart(2, '0');
  const mm = String(end % 60).padStart(2, '0');
  return `${startTime} – ${hh}:${mm}`;
}
